import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';

export interface InvoicePartyDTO {
  name: string;
  companyName: string;
  vatId: string;
  taxNumber: string;
  email: string;
  country: string;
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  state: string;
}

export interface InvoiceLineItemDTO {
  description: string;
  quantity: number;
  unit: string;
  unitNetCents: number;
  lineNetCents: number;
  vatRate: number;
  vatCents: number;
  lineGrossCents: number;
  taxCategory: 'S' | 'AE' | 'G' | 'E' | 'Z' | 'O';
}

export interface InvoiceDTO {
  id: string;
  invoiceNumber: string;
  uid: string;
  issueDate: string | null;
  dueDate: string | null;
  periodFrom: string | null;
  periodTo: string | null;
  customer: InvoicePartyDTO;
  seller: InvoicePartyDTO;
  items: InvoiceLineItemDTO[];
  totals: { netCents: number; vatCents: number; grossCents: number; currency: 'EUR' };
  taxCategory: 'S' | 'AE' | 'G' | 'E' | 'Z' | 'O';
  vatNote: string;
  format: 'pdf_only' | 'pdf_plus_xrechnung_xml' | 'zugferd_pdf';
  pdfStoragePath: string | null;
  xmlStoragePath: string | null;
  molliePaymentId: string | null;
  mollieSubscriptionId: string | null;
  status: 'draft' | 'issued' | 'sent' | 'cancelled';
  sentAt: string | null;
  sentTo: string;
  createdAt: string | null;
  createdBy: string;
}

export interface InvoiceSettingsDTO {
  seller: InvoicePartyDTO;
  iban: string;
  bic: string;
  registerNumber: string;
  registerCourt: string;
  smallBusinessNote: string;
  format: 'pdf_only' | 'pdf_plus_xrechnung_xml' | 'zugferd_pdf';
  invoiceNumberPrefix: string;
  paymentDueDays: number;
  footerText: string;
  senderEmail: string;
  senderName: string;
  replyToEmail: string;
  subjectTemplate: string;
  bodyTemplateOwnerUid: string;
  bodyTemplateId: string;
  emailSendingEnabled: boolean;
  scheduleEnabled: boolean;
  scheduleDayOfMonth: number;
  scheduleHourLocal: number;
  scheduleMinuteLocal: number;
  scheduleTimezone: string;
  lastFiredKey: string;
  lastRunAt: { _seconds?: number; toDate?: () => Date } | null;
}

export function useInvoiceSettings() {
  const { isAdmin } = useUser();
  return useQuery<{ settings: InvoiceSettingsDTO }>({
    queryKey: ['invoice-settings'] as const,
    enabled: isAdmin,
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, { settings: InvoiceSettingsDTO }>(
        functions,
        'adminGetInvoiceSettings',
      );
      const r = await fn({});
      return r.data;
    },
  });
}

export function useUpdateInvoiceSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: InvoiceSettingsDTO) => {
      const fn = httpsCallable<InvoiceSettingsDTO, { ok: true }>(
        functions,
        'adminUpdateInvoiceSettings',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoice-settings'] }),
  });
}

export function useAdminInvoices(uid?: string) {
  const { isAdmin } = useUser();
  return useQuery<{ items: InvoiceDTO[] }>({
    queryKey: ['admin-invoices', uid ?? 'all'] as const,
    enabled: isAdmin,
    staleTime: 15_000,
    queryFn: async () => {
      const fn = httpsCallable<{ uid?: string; limit?: number }, { items: InvoiceDTO[] }>(
        functions,
        'adminListInvoices',
      );
      const r = await fn({ uid, limit: 200 });
      return r.data;
    },
  });
}

export function useMyInvoices() {
  const { user } = useUser();
  return useQuery<{ items: InvoiceDTO[] }>({
    queryKey: ['my-invoices', user?.uid] as const,
    enabled: !!user,
    staleTime: 15_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, { items: InvoiceDTO[] }>(
        functions,
        'listMyInvoices',
      );
      const r = await fn({});
      return r.data;
    },
  });
}

export function usePreviewInvoice() {
  return useMutation({
    mutationFn: async (input: {
      uid: string;
      periodFromMs: number;
      periodToMs: number;
      fallbackNetCents?: number;
      fallbackDescription?: string;
    }) => {
      const fn = httpsCallable<typeof input, { invoice: InvoiceDTO; pdfBase64: string }>(
        functions,
        'adminPreviewInvoice',
      );
      const r = await fn(input);
      return r.data;
    },
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      uid: string;
      periodFromMs: number;
      periodToMs: number;
      fallbackNetCents?: number;
      fallbackDescription?: string;
      send: boolean;
    }) => {
      const fn = httpsCallable<
        typeof input,
        { invoiceId: string; invoiceNumber: string; status: string }
      >(functions, 'adminCreateInvoice');
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-invoices'] });
      qc.invalidateQueries({ queryKey: ['my-invoices'] });
    },
  });
}

export function useResendInvoice() {
  return useMutation({
    mutationFn: async (input: { uid: string; invoiceId: string }) => {
      const fn = httpsCallable<typeof input, { ok: true }>(functions, 'adminResendInvoice');
      const r = await fn(input);
      return r.data;
    },
  });
}

export function useAdminInvoiceDownload() {
  return useMutation({
    mutationFn: async (input: { uid: string; invoiceId: string; kind: 'pdf' | 'xml' }) => {
      const fn = httpsCallable<typeof input, { url: string }>(
        functions,
        'adminGetInvoiceDownloadUrl',
      );
      const r = await fn(input);
      return r.data.url;
    },
  });
}

export function useMyInvoiceDownload() {
  return useMutation({
    mutationFn: async (input: { invoiceId: string; kind: 'pdf' | 'xml' }) => {
      const fn = httpsCallable<typeof input, { url: string }>(
        functions,
        'getMyInvoiceDownloadUrl',
      );
      const r = await fn(input);
      return r.data.url;
    },
  });
}

// Loads the calling admin's mail-templates so the InvoiceSettings page
// can show a dropdown.
export interface MailTemplateRow {
  id: string;
  name: string;
  subject?: string;
  category?: string;
}
export function useMyMailTemplates() {
  const { user } = useUser();
  return useQuery<{ items: MailTemplateRow[] }>({
    queryKey: ['my-mail-templates'] as const,
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, { items: MailTemplateRow[] }>(
        functions,
        'listMailTemplates',
      );
      const r = await fn({});
      return r.data;
    },
  });
}

export function useBroadcastMail() {
  return useMutation({
    mutationFn: async (input: {
      templateOwnerUid: string;
      templateId: string;
      audience: 'all_active_beginner' | 'all_users' | 'test_only';
      testEmail?: string;
      subjectOverride?: string;
    }) => {
      const fn = httpsCallable<
        typeof input,
        { ok: true; sent: number; errors: number; audience: string }
      >(functions, 'adminBroadcastMail');
      const r = await fn(input);
      return r.data;
    },
  });
}

export function formatEUR(cents: number): string {
  return (cents / 100).toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
